import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { useSession } from "@/services/auth0/session";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Types
export interface DispatchCenterVolumeData {
  date: string;
  dispatchCenterId: string | null;
  dispatchCenterName?: string;
  volume: number;
}

export interface CustomerVolumeData {
  date: string;
  customerId: string | null;
  customerName: string;
  volume: number;
}

export interface NewCustomerData {
  date: string;
  count: number;
}

export interface CustomerRevenueData {
  date: string;
  customerId: string | null;
  customerName: string;
  revenue: number;
  orderCount: number;
}

// Helper function to format date range for API queries
export const formatDateRangeParams = (dateRange: DateRange) => {
  const params: Record<string, string | number> = {};
  if (dateRange.from) {
    params.startDate = format(dateRange.from, "yyyy-MM-dd");
  }
  if (dateRange.to) {
    params.endDate = format(dateRange.to, "yyyy-MM-dd");
  }
  return params;
};

// Daily order volume by dispatch center
export const useDailyOrderVolumeByDispatchCenter = (
  dateRange: DateRange,
  dispatchCenterId?: string
) => {
  const { token } = useSession();

  return useQuery<DispatchCenterVolumeData[]>({
    queryKey: [
      "analytics",
      "dailyOrderVolumeByDispatchCenter",
      dateRange,
      dispatchCenterId,
    ],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (dispatchCenterId) {
        params.dispatchCenterId = dispatchCenterId;
      }

      const response = await axios.get<DispatchCenterVolumeData[]>(
        `${apiUrl}/api/v2/analytics/orders/volume/daily/by-dispatch-center`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Daily order volume by customer
export const useDailyOrderVolumeByCustomer = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerVolumeData[]>({
    queryKey: [
      "analytics",
      "dailyOrderVolumeByCustomer",
      dateRange,
      customerId,
    ],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<CustomerVolumeData[]>(
        `${apiUrl}/api/v2/analytics/orders/volume/daily/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// New customers daily
export const useNewCustomersDaily = (dateRange: DateRange) => {
  const { token } = useSession();

  return useQuery<NewCustomerData[]>({
    queryKey: ["analytics", "newCustomersDaily", dateRange],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);

      const response = await axios.get<NewCustomerData[]>(
        `${apiUrl}/api/v2/analytics/customers/new/daily`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Daily revenue by customer
export const useDailyRevenueByCustomer = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerRevenueData[]>({
    queryKey: ["analytics", "dailyRevenueByCustomer", dateRange, customerId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<CustomerRevenueData[]>(
        `${apiUrl}/api/v2/analytics/revenue/daily/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Weekly revenue by customer
export const useWeeklyRevenueByCustomer = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerRevenueData[]>({
    queryKey: ["analytics", "weeklyRevenueByCustomer", dateRange, customerId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<CustomerRevenueData[]>(
        `${apiUrl}/api/v2/analytics/revenue/weekly/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Monthly revenue by customer
export const useMonthlyRevenueByCustomer = (
  year?: number,
  month?: number,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerRevenueData[]>({
    queryKey: [
      "analytics",
      "monthlyRevenueByCustomer",
      year,
      month,
      customerId,
    ],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (year !== undefined) params.year = year;
      if (month !== undefined) params.month = month;
      if (customerId) params.customerId = customerId;

      const response = await axios.get<CustomerRevenueData[]>(
        `${apiUrl}/api/v2/analytics/revenue/monthly/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (year !== undefined || month !== undefined) && !!token,
  });
};

// === New Dashboard Analytics Types ===

export interface AlertsData {
  failedTasks: {
    count: number;
    breakdown: { reason: string; count: number }[];
    items: {
      taskId: string;
      taskNumber: number;
      shipmentNumber?: number | null;
      orderNumber?: number;
      customerName: string;
      reason: string;
      timestamp: string;
    }[];
  };
  pendingOrders: {
    count: number;
    threshold: string;
    items: {
      orderId: string;
      orderNumber: number;
      customerName: string;
      hoursAgo: number;
    }[];
  };
  decliningCustomers: {
    count: number;
    threshold: string;
    items: {
      customerId: string;
      customerNumber?: number;
      name: string;
      previousOrders: number;
      currentOrders: number;
      changePercent: number;
    }[];
  };
}

export interface ScorecardData {
  date: string;
  orders: {
    count: number;
    vsYesterday: { value: number; percent: number };
    vsAverage: { value: number; percent: number };
  };
  tasks: {
    total: number;
    completed: number;
    failed: number;
    inProgress: number;
    pending: number;
    successRate: number;
    vsAverageSuccessRate: number;
  };
  revenue: {
    cents: number;
    vsYesterday: { cents: number; percent: number };
    vsAverage: { cents: number; percent: number };
  };
}

export interface OperatorStats {
  operatorId: string;
  operatorName: string;
  depot: string;
  depotId: string;
  tasks: {
    total: number;
    completed: number;
    failed: number;
    inProgress: number;
    pending: number;
  };
  shipmentsHandled: number;
  avgTaskTimeMinutes: number | null;
  totalTimeMinutes: number;
  efficiencyScore: number;
  successRate: number;
  trend: "up" | "down" | "stable";
  failureReasons: { reason: string; code: number; count: number }[];
}

export interface OperatorEfficiencyData {
  operators: OperatorStats[];
  benchmarks: {
    avgTasksPerOperator: number;
    avgTaskTimeMinutes: number;
    avgSuccessRate: number;
  };
  period: {
    start: string;
    end: string;
  };
}

export interface CustomerHealthData {
  period: {
    start: string;
    end: string;
    comparedTo: { start: string; end: string };
  };
  growing: {
    customerId: string;
    customerNumber?: number;
    name: string;
    previousPeriodOrders: number;
    currentPeriodOrders: number;
    changePercent: number;
    revenueCents: number;
  }[];
  declining: {
    customerId: string;
    customerNumber?: number;
    name: string;
    previousPeriodOrders: number;
    currentPeriodOrders: number;
    changePercent: number;
    revenueCents: number;
  }[];
  topByRevenue: {
    customerId: string;
    customerNumber?: number;
    name: string;
    orderCount: number;
    revenueCents: number;
    percentOfTotal: number;
  }[];
  concentrationRisk: {
    top3Percent: number;
    isHighRisk: boolean;
  };
}

// === New Dashboard Analytics Hooks ===

// Alerts - problems needing attention
export const useAnalyticsAlerts = () => {
  const { token } = useSession();

  return useQuery<AlertsData>({
    queryKey: ["analytics", "alerts"],
    queryFn: async () => {
      const response = await axios.get<AlertsData>(
        `${apiUrl}/api/v2/analytics/alerts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token,
    refetchInterval: 60000, // Refresh every minute
  });
};

// Scorecard - today's metrics with comparisons
export const useAnalyticsScorecard = (date?: string) => {
  const { token } = useSession();

  return useQuery<ScorecardData>({
    queryKey: ["analytics", "scorecard", date],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (date) params.date = date;

      const response = await axios.get<ScorecardData>(
        `${apiUrl}/api/v2/analytics/scorecard`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token,
  });
};

// Operator efficiency
export const useOperatorEfficiency = (
  dateRange: DateRange,
  dispatchCenterId?: string
) => {
  const { token } = useSession();

  return useQuery<OperatorEfficiencyData>({
    queryKey: ["analytics", "operatorEfficiency", dateRange, dispatchCenterId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (dispatchCenterId) params.dispatchCenterId = dispatchCenterId;

      const response = await axios.get<OperatorEfficiencyData>(
        `${apiUrl}/api/v2/analytics/operators/efficiency`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!dateRange.from && !!dateRange.to && !!token,
  });
};

// Customer health
export const useCustomerHealth = (dateRange: DateRange) => {
  const { token } = useSession();

  return useQuery<CustomerHealthData>({
    queryKey: ["analytics", "customerHealth", dateRange],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);

      const response = await axios.get<CustomerHealthData>(
        `${apiUrl}/api/v2/analytics/customers/health`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!dateRange.from && !!dateRange.to && !!token,
  });
};

export interface CumulativeOrderData {
  date: string;
  dailyCount: number;
  dailyRevenueCents: number;
  cumulativeCount: number;
  cumulativeRevenueCents: number;
}

export const useCumulativeOrderAnalytics = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<{ data: CumulativeOrderData[] }>({
    queryKey: ["analytics", "cumulativeOrders", dateRange, customerId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<{ data: CumulativeOrderData[] }>(
        `${apiUrl}/api/v2/analytics/orders/cumulative`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    },
    enabled: !!dateRange.from && !!dateRange.to && !!token,
  });
};
